import {useState, useEffect} from "react";
import axios from 'axios';
import {HelioviewerModalLeftPanel} from './helioviewermodalleftpanel.js';
import {HELIOVIEWERERRORCODES} from './../../constants.js';
import {HelioviewerModalWarningBox} from './helioviewermodalwarningbox.js';
import {HelioviewerModalErrorBox} from './helioviewermodalerrorbox.js';

/**
 * Modal content holds the content/components interacting and display data from CDAWeb server.
 *
 * @param props.title String title of modal.
 * @param props.mission String of mission names.
 * @param props.helioviewerSessionState Array of data that is used to save state of image setting.
 * @param props.setHelioviewerSessionState Function that deals with setting the sessionState parameter.
 * @return JSX
 */
export function HelioviewerModalContent(props) {
    const { title, mission, helioviewerSelection, helioviewerSessionState, setHelioviewerSessionState} = props;
    let visibleContent, errorContent;

    const [data, setData] = useState(helioviewerSessionState?.data || null);
    const [loading, setLoading] = useState(false);
    // eslint-disable-next-line
    const [selection, setSelection] = useState( helioviewerSessionState?.selection || helioviewerSelection || []);
    const [missionComponents, setMissionComponents] = useState( helioviewerSessionState?.missionComponents || {});
    const [helioviewerInfo, setHelioviewerInfo] = useState( helioviewerSessionState?.helioviewerInfo || null);
    const [helioviewerSelectionMap, setHelioviewerSelectionMap] = useState( helioviewerSessionState?.helioviewerSelectionMap || {});
    const [startTime, setStartTime] = useState(helioviewerSessionState?.startTime || null);
    const [imageTimestamp, setImageTimestamp] = useState(helioviewerSessionState?.imageTimestamp  || null);
    const [currentTimeUnit, setCurrentTimeUnit] = useState("Seconds");
    const [numberOfTimeUnits, setNumberOfTimeUnits] = useState(1);
    const [helioviewerError, setHelioviewerError] = useState(null);
    const [type, setType] = useState(0);
    const [latestTime, setLatestTime] = useState(null);
    const [earliestTime, setEarliestTime] = useState(null);

    const timeUnits = ["Seconds", "Minutes", "Hours", "Days", "Months", "Years"];
    const timeUnitOptionsJsx = [];

    for(const timeUnit of timeUnits) {
        timeUnitOptionsJsx.push(<option value={timeUnit} key={timeUnit}>{timeUnit}</option>);
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get(`/heliodata/app/load_sdac_data/${mission}`);

                if(response.data.data.length === 0) {
                    let err = new Error('');
                    err.response = {};
                    err.response.status =  "003";
                    throw err;
                }

                const groupData = (data) => {
                    return data.reduce((acc, childObj) => {
                        const key = Object.keys(childObj)[0];
                        const child = childObj[key];
                        const { type, item } = child;

                        if (!type || !item) return acc;

                        if (!acc[type]) {
                            acc[type] = {};
                        }

                        if (!acc[type][item]) {
                            acc[type][item] = [];
                        }

                        acc[type][item].push({ id: key, ...child });

                        return acc;
                    }, {});
                };

                const groupedData = groupData(response.data.data);

                setHelioviewerInfo(response.data.data);
                setHelioviewerSelectionMap(groupedData);
                setMissionComponents(response.data.components);
                updateSession();
            } catch (error) {
                const status = error.response?.status ?? 'Unknown';

                const messages = {
                    '003': 'No data available for this mission.',
                    '503':   'Helioviewer is currently unavailable. Please try again later.',
                    '504':   'Helioviewer timed out. The service may be down.',
                };

                const message = messages[status] ?? 'Error fetching mission attributes.';
                setHelioviewerError({ status, message });
            }
        };

        if (!helioviewerSessionState) {
            fetchData();
        }

        const currentSelector = findBySourceId(helioviewerSelectionMap, selection.sourceId);

        if(currentSelector) {
            setEarliestTime(currentSelector.start || "");
            setLatestTime(currentSelector.end || "");
        }
        // eslint-disable-next-line
    }, [helioviewerSessionState, helioviewerSelection, mission, startTime]);

    const findBySourceId = (obj, targetSourceId, k = null) => {
        if (typeof obj !== 'object' || obj === null) return null;

        if (Array.isArray(obj)) {
            const res = (obj.find(item => item?.sourceId === parseInt(targetSourceId)))
            return res ? {parent: k, ...res} : null;
        }

        for (const key in obj) {
            const result = findBySourceId(obj[key], targetSourceId, key);
            if (result) return result;
        }

        return null;
    };

    const displayLoading = () => {
        setHelioviewerError(null);
        setLoading(true);
    }

    const updateSession = () => {
        const sessionState = {};
        sessionState.selection = selection;
        sessionState.data = data;
        sessionState.latestTime = latestTime;
        sessionState.earliestTime = earliestTime;
        sessionState.startTime = startTime;
        sessionState.helioviewerInfo = helioviewerInfo;
        sessionState.imageTimestamp = imageTimestamp;
        sessionState.helioviewerSelectionMap = helioviewerSelectionMap;
        sessionState.missionComponents = missionComponents;
        sessionState.setHelioviewerSelectionMap = setHelioviewerSelectionMap;
        setHelioviewerSessionState(sessionState);
    }

    const onStartTimeChange = (val) => {
        setStartTime(val);
    }

    const handleSelectionChange = (val) => {
        const cSelector = findBySourceId(helioviewerSelectionMap, val);
        const newSelection = {
                        type: cSelector?.type,
                        sourceId: cSelector?.sourceId,
                        instrument: missionComponents[val]?.["Instrument"] || null,
                        measurement: missionComponents[val]?.["Measurement"] || null,
                        energyBand: missionComponents[val]?.["Energy Band"] || null,
                        reconstruction: missionComponents[val]?.["Reconstruction"] || null,
                        detector: missionComponents[val]?.["Detector"] || null,
                        filter: missionComponents[val]?.["Filter"] || null,
                        filterWheel1: missionComponents[val]?.["Filter Wheel 1"] || null,
                        filterWheel2: missionComponents[val]?.["Filter Wheel 2"] || null
                      };

            setSelection(newSelection);
    }

    const handleFindImage = async(imageDate = null) => {
        let date = imageDate;
        let imageStartTime = earliestTime.replace(" ", "T") + "Z";
        let imageEndTime =  latestTime.replace(" ", "T")  + "Z";

        if(!date)
        {
            date = startTime ? `${startTime.toISOString().slice(0,19)+"Z"}` : imageEndTime;
        }

        try {
            const isFutureTime = (new Date(date)) > Date.now();
            const isTimeOutOfRange = (new Date(date)) > (new Date(imageEndTime))
                                     ||
                                     (new Date(date)) < (new Date(imageStartTime));
            if(isFutureTime || isTimeOutOfRange)
            {
                let err = new Error('');
                err.response = {};
                err.response.status = isFutureTime ? "001" : "002";
                throw err;
            }

            displayLoading();
            const response = await axios.get(
            `/heliodata/app/load_sdac_image/${selection.sourceId}/${date}/${type}`,
            {responseType: 'arraybuffer'}
            );
            const blob = new Blob([response.data], { type: `image/jpeg` });
            const imageUrl = URL.createObjectURL(blob);
            setData(imageUrl);
            setImageTimestamp(response.headers.helioviewerimagedate);
            setHelioviewerError(null);
            updateSession();
        } catch(error) {
            setHelioviewerError({status: error.response?.status || 'Unknown', message: error.message});
        } finally {
            setLoading(false);
        }
    }

    const handleDownload = () => {
        if (!data) {
            setHelioviewerError({status:'Unknown', message:"No image available to download. Please find an image first!"});
            return;
        }

        try{
            const link = document.createElement("a");
            link.href = data;
            link.download = `heliodata_image_${imageTimestamp || "unknown"}.jpg`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch(error) {
           error['insight'] = "Download error";
           setHelioviewerError({status: error.response?.status || 'Unknown', message: error.message});
        }
    };

    const handleNewWindow = () => {
        try{
            const width = window.innerWidth;
            const height = window.innerHeight;
            const left = window.screenX + (window.outerWidth - width) / 2;
            const top = window.screenY + (window.outerHeight - height) / 2;
            window.open(data, '_blank', `width=${width},height=${height},left=${left},top=${top},noopener,noreferrer`);
        } catch(error) {
            error['insight'] = "New window error";
            setHelioviewerError({status:'Unknown', message:error.message});
        }
    }

    const handlePan = async (moveLeft = false) => {
        displayLoading();
        let date = new Date(imageTimestamp);
        date = changeTime(date, currentTimeUnit, moveLeft, onStartTimeChange, numberOfTimeUnits);

        const toLocalISOString = (date = new Date()) => {
            const pad = (n) => String(n).padStart(2, '0');
            const year = date.getFullYear();
            const month = pad(date.getMonth() + 1);
            const day = pad(date.getDate());
            const hour = pad(date.getHours());
            const minute = pad(date.getMinutes());
            const second = pad(date.getSeconds());
            const ms = String(date.getMilliseconds()).padStart(3, '0');

            return `${year}-${month}-${day}T${hour}:${minute}:${second}.${ms}`;
        }

        const localStr = toLocalISOString(date)+"Z";
        onStartTimeChange(new Date(localStr));
        await handleFindImage(localStr);
        updateSession();
    }

    if (loading) {
       visibleContent = getLoadingJSX();
    }

    if (helioviewerError) {
       errorContent = getHelioviewerErrorJSX(helioviewerError);
    }

    if (data) {
      visibleContent = (
                     <div className="helioviewerRightPanel">
                       {data &&
                         <div className="helioviewerImageContainer" onClick={()=>handleNewWindow()}>
                           <img src={data} alt="Downloaded" className="helioviewerImage" />
                         </div>
                       }
                       <div className="helioviewerButtonRowContainer">
                         <button
                           className={`helioviewerModalButtonWide`}
                           onClick={()=>handlePan(true)}
                           disabled={false}>{"<< Previous"}
                         </button>
                         {currentTimeUnit && (
                           <select
                             className="helioviewerFieldValueInput helioviewerInputWide"
                             defaultValue = {1}
                             onChange={event => setNumberOfTimeUnits(event.target.value)}>
                             {
                               Array.from(
                                 Array(
                                   currentTimeUnit.toLowerCase() === "seconds" || currentTimeUnit.toLowerCase() === "minutes"
                                   ? 60
                                   : currentTimeUnit.toLowerCase() === "hours"
                                   ? 24
                                   : currentTimeUnit.toLowerCase() === "days"
                                   ? 31
                                   : currentTimeUnit.toLowerCase() === "months"
                                   ? 12
                                   : currentTimeUnit.toLowerCase() === "years"
                                   ? 20
                                   : 0
                                 ).keys()
                               ).map(i => (
                                 <option key={i + 1} value={i + 1}>
                                   {i + 1}
                                 </option>
                             ))}
                           </select>
                         )}
                         <select
                           className="helioviewerFieldValueInput helioviewerInputWide"
                           defaultValue={timeUnits[0]}
                           onChange={event => setCurrentTimeUnit(event.target.value)}>
                           {timeUnitOptionsJsx}
                         </select>

                         <button
                           className={`helioviewerModalButtonWide`}
                           onClick={()=>handlePan(false)}
                           disabled={false}>{"Next >>"}
                         </button>
                       </div>
                     </div>);
    }

     return (
         <div className="">
           <div className="helioviewerTitleContainer">
             <div className="helioviewerTitleLeftColumn">
             </div>
             <div className="helioviewerTitleRightColumn">
             {title}
             </div>
           </div>
           <div className="helioviewerContainer">
             <div className="helioviewerItem">
               <HelioviewerModalLeftPanel
                 type = {type}
                 data = {data}
                 loading = {loading}
                 startTime = {startTime}
                 onStartTimeChange = {onStartTimeChange}
                 imageTimestamp = {imageTimestamp}
                 mission = {mission}
                 selection = {{startTime: latestTime, endTime: earliestTime,...selection}}
                 helioviewerSelectionMap = {helioviewerSelectionMap}
                 handleFindImage = {handleFindImage}
                 handleDownload = {handleDownload}
                 handleSelectionChange = {handleSelectionChange}
                 setType = {setType}
                />
             </div>
             <div className="helioviewerItem">
               {
                 helioviewerError ? <div>{errorContent}</div> : <div></div>
               }
               {
                 data
                 ?
                 <div>
                   {visibleContent}
                 </div>
                 : loading ? <div className="helioviewerRightPanel">{visibleContent}</div> :
                 <div className="helioviewerRightPanel helioviewer28Font">
                   <div className="helioviewerImagePlaceholder">Select Time and Click "Find Image" Button</div>
                 </div>}
             </div>
           </div>
         </div>
     )
}


/**
 * Function that returns JSX for loading.
 * @return JSX
 */
function getLoadingJSX() {
    return (
      <div className="helioviewerModalLoadingContainer">
        <div className="helioviewerModalLoadingSpinnerBig"></div>
      </div>
    );
}


/**
 * Function that returns JSX for error.
 * @param helioviewerError Object that contains 'status' and 'message'
 * @return JSX
 */
function getHelioviewerErrorJSX(helioviewerError) {
    let errorContent = (<div>Error</div>);

    if(`${helioviewerError.status}` === '001' || `${helioviewerError.status}` === '006') {
        errorContent = <HelioviewerModalWarningBox title= {HELIOVIEWERERRORCODES[`${helioviewerError.status}`][0]} body={HELIOVIEWERERRORCODES[`${helioviewerError.status}`][1]} insightMessage = {helioviewerError.message.split('.')[0]} />
    } else if(`${helioviewerError.status}` === '002') {
        errorContent = <HelioviewerModalWarningBox title= {HELIOVIEWERERRORCODES['002'][0]} body={HELIOVIEWERERRORCODES['002'][1]} />
    } else if(parseInt(`${helioviewerError.status}`) < 100) {
        errorContent = <HelioviewerModalWarningBox title= {HELIOVIEWERERRORCODES[`${helioviewerError.status}`][0]} body={HELIOVIEWERERRORCODES[`${helioviewerError.status}`][1]} />
    } else if( Object.keys(HELIOVIEWERERRORCODES).includes(`${helioviewerError.status}`)) {
        errorContent = <HelioviewerModalErrorBox title= {HELIOVIEWERERRORCODES[helioviewerError.status][0]} body={helioviewerError.message} />
    } else {
        errorContent = <HelioviewerModalErrorBox title= {HELIOVIEWERERRORCODES['Unknown'][0]} body={HELIOVIEWERERRORCODES['Unknown'][1]} />
    }
    return errorContent
}


/**
 * Adjusts a given date by a specified time unit and direction, then triggers a callback with the updated date
 * @param {Date} date - The date object to be modified.
 * @param {string} unit - The unit of time to adjust. Valid options are "Seconds", "Minutes", "Hours", "Days", "Months", "Years".
 * @param {boolean} moveLeft - If true, the time will be moved backward (decreased). If false, it will be moved forward (increased).
 * @param {function} onStartTimeChange - A callback function that will be called with the updated date after modification.
 *
 */
function changeTime(date, unit, moveLeft, onStartTimeChange, numberOfUnits = 1){
    let change = moveLeft ? -1* parseInt(numberOfUnits) : parseInt(numberOfUnits);

    switch(unit) {
      case "Seconds":
        date.setSeconds(date.getSeconds() + change);
        break;
      case "Minutes":
        date.setMinutes(date.getMinutes() + change);
        break;
      case "Hours":
        date.setHours(date.getHours() + change);
        break;
      case "Days":
        date.setDate(date.getDate() + change);
        break;
      case "Months":
        date.setMonth(date.getMonth() + change);
        break;
      case "Years":
        date.setFullYear(date.getFullYear() + change);
        break;
      default:
        console.error("Invalid time unit");
    }

    onStartTimeChange(date);
    return date;
};
